// Look on a phone: a dot at the screen's centre, and a tap clicks what it is on (Josh 10-17: "when clicking over to
// look mode, I don't think it should zoom to the window unless I do double tap; otherwise I want the floating dot and a
// tap clicks things"). The flow: docs/flows/look-mode.yaml.
//
// While Look is on a touch screen and no window is brought up, a clear pad lies over the arc (under the bars, the
// sheets and ‹ Back) and takes every finger, so nothing in a window acts where the finger happens to land:
// - the dot (#reticle, the page's own) stays at the centre: dim over nothing, brighter over a window, larger over
//   something to press, which shows its `.hover` as a mouse resting on it would;
// - a tap flashes the dot and presses what is under it — a real mouse's press there (pointer and mouse down and up,
//   then the click), so buttons, rows, links and a stream's picture (the Mac's pointer goes to the dot) all act as they
//   do for a mouse. Every window can be brought up by a double tap, so a tap on one waits out the double-tap window
//   (DOUBLE_MS) before it presses, the flash saying it was taken; a field is focused at once instead, inside the tap
//   itself, or the phone's keyboard would not come up; over no window nothing waits;
// - a double tap brings up the window under the dot (render-dom.js zoomTo); then the pad and the dot go and the window
//   takes taps directly, until ‹ Back or a pinch in (dom-input.js);
// - one finger dragged scrolls whatever scrolls under the dot, at the window's own scale; without the phone's
//   orientation (refused, or a tablet without it) the drag turns the view instead — the only way to aim the dot then;
// - the first time Look opens, one line under the dot says how (`HINT`).
// A desktop's mouse is untouched (no dot, clicks go where the mouse is), and so is the headset (core/xrinput.js).
//
// One seam: `new LookDot(world)`; the world calls `on()` (Look begins), `off()` (Look ends: nothing of it left),
// `sync()` (a window brought up or put back, suspended, resumed) and `probe()` (the view turned).
const ACTOR = 'button, a, [data-act], input, textarea, select, [role="button"], li[data-row], [contenteditable="true"]'
const FIELD = 'input:not([type=button]):not([type=submit]):not([type=checkbox]):not([type=radio]), textarea, select, [contenteditable="true"]'
export const DOUBLE_MS = 260   // a second tap within this is a double tap (under iOS's own 300 ms)
const MOVE = 10                 // a finger moving further than this (CSS px) is a drag, not a tap
export const HINT = 'Point the dot · tap to click · double-tap to zoom'
const HINT_KEY = 'cody.lookHint'
const coarse = () => { try { return matchMedia('(pointer: coarse)').matches } catch { return false } }

export class LookDot {
  constructor(world) {
    this.w = world
    this.active = false
    this.pending = null   // a tap waiting out the double-tap window: { timer, at, target, spec }
    this.press = null     // the finger on the pad
    this.dot = document.getElementById('reticle')
    if (!this.dot) { this.dot = document.createElement('div'); this.dot.id = 'reticle'; document.body.appendChild(this.dot) }
    this.dot.classList.add('look-dot')
    this.dot.hidden = true
    this.pad = document.createElement('div')
    this.pad.className = 'dw-look-pad'
    this.pad.hidden = true
    world.stage.appendChild(this.pad)
    this.pad.addEventListener('pointerdown', (e) => this.down(e))
    this.pad.addEventListener('pointermove', (e) => this.move(e))
    this.pad.addEventListener('pointerup', (e) => this.up(e))
    this.pad.addEventListener('pointercancel', () => { this.press = null })
    // the pad's own clicks (the browser's, from the finger) are nobody's
    this.pad.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault() })
  }

  /// Look began: the dot, if this is a touch screen.
  on() {
    this.active = coarse()
    this.sync()
    if (!this.active) return
    clearInterval(this.timer)
    this.timer = setInterval(() => this.probe(), 200)   // what is under the dot changes as windows redraw, too
    this.hintOnce()
  }
  /// Look ended: nothing of it left — no pad over the windows, no dot, no tap waiting, no hint, no highlight.
  off() {
    this.active = false
    clearInterval(this.timer); this.timer = null
    this.cancel()
    this.press = null
    this.hint?.remove(); this.hint = null
    this.sync()
  }
  /// The pad and the dot shown when they are wanted: Look, a touch screen, nothing brought up, not in the room.
  sync() {
    const shown = this.active && this.w.mode === 'look' && !this.w.zoomed && !this.w.suspended
    this.pad.hidden = !shown
    this.dot.hidden = !shown
    if (!shown) { this.cancel(); this.lit(null) }
    else this.probe()
  }

  // MARK: what is under the dot

  centre() { return { x: innerWidth / 2, y: innerHeight / 2 } }
  /// The element under the dot: the first one there that is not the pad, the dot or the hint.
  target() {
    const { x, y } = this.centre()
    const all = document.elementsFromPoint?.(x, y) || []
    return all.find((el) => el !== this.pad && el !== this.dot && el !== this.hint && !this.dot.contains(el)) || null
  }
  /// The window an element is in, if it is on the arc.
  windowOf(el) {
    const slot = el?.closest?.('.dw-slot')
    if (!slot) return null
    for (const rec of this.w.recs.values()) if (rec.slot === slot) return rec.spec
    return null
  }
  probe() {
    if (this.dot.hidden) return
    const el = this.target(), actor = el?.closest?.(ACTOR) || null
    this.dot.classList.toggle('on-window', !!this.windowOf(el))
    this.dot.classList.toggle('over', !!actor && !actor.disabled)
    this.lit(actor)
  }
  lit(el) { this.w.input?.hover?.(el) }

  // MARK: the finger on the pad

  down(e) {
    if (this.pad.hidden) return
    if (this.press) { this.press = null; return }   // a second finger: neither a tap nor a drag
    this.press = { id: e.pointerId, x: e.clientX, y: e.clientY, at: performance.now(), dragging: false }
  }
  move(e) {
    const p = this.press
    if (!p || p.id !== e.pointerId) return
    const dx = e.clientX - p.x, dy = e.clientY - p.y
    if (!p.dragging && Math.hypot(dx, dy) < MOVE) return
    p.dragging = true
    p.x = e.clientX; p.y = e.clientY
    if (this.w.input?.oriented) this.scroll(dx, dy)
    else this.turn(dx, dy)
  }
  up(e) {
    const p = this.press
    this.press = null
    if (!p || p.id !== e.pointerId || p.dragging) return
    this.tap()
  }

  /// One finger dragged without orientation: the view turns, as dragging the background does on a desktop.
  turn(dx, dy) {
    this.w.yaw += dx * 0.15
    this.w.pitch = Math.max(-60, Math.min(60, this.w.pitch + dy * 0.15))
    this.w.aim()
  }
  /// One finger dragged with orientation: whatever scrolls under the dot follows the finger (at the window's scale).
  scroll(dx, dy) {
    for (let el = this.target(); el && el !== this.w.stage; el = el.parentElement) {
      const s = getComputedStyle(el)
      const canY = el.scrollHeight > el.clientHeight + 1 && /(auto|scroll)/.test(s.overflowY)
      const canX = el.scrollWidth > el.clientWidth + 1 && /(auto|scroll)/.test(s.overflowX)
      if (!canY && !canX) continue
      const r = el.getBoundingClientRect(), k = (r.height / (el.offsetHeight || r.height || 1)) || 1   // on-screen px per CSS px
      if (canY) el.scrollTop -= dy / k
      if (canX) el.scrollLeft -= dx / k
      return
    }
  }

  /// A tap: the second within DOUBLE_MS on a window brings it up; a first presses what is under the dot.
  tap() {
    const now = performance.now()
    const was = this.pending
    if (was && now - was.at < DOUBLE_MS && was.spec) {
      this.cancel()
      this.w.zoomTo(was.spec)
      return
    }
    this.cancel()
    this.flash()
    const target = this.target(), spec = this.windowOf(target)
    if (target?.closest?.(FIELD)) {
      // a field: focused now, inside the tap (the keyboard comes up only from the tap itself); a second tap still zooms
      this.pressAt(target)
      if (spec) this.pending = { at: now, spec, timer: setTimeout(() => { this.pending = null }, DOUBLE_MS) }
      return
    }
    if (!spec) { this.pressAt(target); return }   // no window to bring up: nothing to wait for
    this.pending = { at: now, spec, timer: setTimeout(() => { this.pending = null; this.pressAt(target) }, DOUBLE_MS) }
  }
  cancel() { if (this.pending) clearTimeout(this.pending.timer); this.pending = null }
  flash() {
    this.dot.classList.remove('press')
    void this.dot.offsetWidth   // the flash again for a quick second tap
    this.dot.classList.add('press')
    clearTimeout(this.flashTimer)
    this.flashTimer = setTimeout(() => this.dot.classList.remove('press'), 180)
  }
  /// A mouse's press on `el` at the dot: pointer and mouse down and up, the click; a field focused (and its keyboard).
  pressAt(el) {
    if (!el || !el.isConnected) return
    const { x, y } = this.centre()
    const base = { bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y, button: 0, view: window }
    const P = typeof PointerEvent === 'function' ? PointerEvent : MouseEvent
    el.dispatchEvent(new P('pointerdown', { ...base, pointerId: 41, pointerType: 'mouse', isPrimary: true, buttons: 1 }))
    el.dispatchEvent(new MouseEvent('mousedown', { ...base, buttons: 1 }))
    const field = el.closest?.(FIELD)
    if (field) { try { field.focus({ preventScroll: true }) } catch {} }   // focus: gesture (the tap on the pad)
    el.dispatchEvent(new P('pointerup', { ...base, pointerId: 41, pointerType: 'mouse', isPrimary: true }))
    el.dispatchEvent(new MouseEvent('mouseup', base))
    el.dispatchEvent(new MouseEvent('click', { ...base, detail: 1 }))
  }

  // MARK: the hint, once

  hintOnce() {
    let seen = false
    try { seen = localStorage.getItem(HINT_KEY) === '1'; localStorage.setItem(HINT_KEY, '1') } catch {}
    if (seen) return
    this.hint = document.createElement('div')
    this.hint.className = 'dw-look-hint glass'
    this.hint.setAttribute('role', 'status')
    this.hint.textContent = HINT
    document.body.appendChild(this.hint)
    const hint = this.hint
    setTimeout(() => { hint.classList.add('gone'); setTimeout(() => { hint.remove(); if (this.hint === hint) this.hint = null }, 400) }, 4500)
  }
}
